
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import MDBox from "components/MDBox";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import { normalizeContentType } from "../components/ChartDataProcessor"; // Import our new utility
import { generateColors } from "utils/chartColors";

function OptimalContentFormatChart({
    title,
    datasets = [],
    previousdatasets = [],
    isLoading = false,
    isEntitled = true,
    featureName = "",
    planRequired = "",
    entitlements // ✅ Added prop
}) {
    const safeDatasets = Array.isArray(datasets) ? datasets : [];

    // ----------------------------------------------------------------------
    // AGGREGATION LOGIC
    // ----------------------------------------------------------------------
    const processedData = useMemo(() => {
        // 1. Define Categories
        const categories = ['Short Video', 'Long Video', 'Static Image', 'Collection', 'Text/Link'];

        // Structure: { 'Short Video': { total: 0, count: 0 }, ... } per profile
        const profileAggregations = safeDatasets.map(profile => {
            const bins = {};
            categories.forEach(c => bins[c] = { total: 0, count: 0 });
            return bins;
        });

        // 2. Process Each Profile
        safeDatasets.forEach((profile, profileIndex) => {
            const platform = profile.platform || 'instagram'; // default fallback
            const posts = profile.data || [];
            const bins = profileAggregations[profileIndex];

            posts.forEach(post => {
                let actualPosts = [];
                if (post.posts) actualPosts = post.posts; // Calendar entry
                else if (post.videos) actualPosts = post.videos; // YouTube calendar entry
                else actualPosts = [post]; // Direct post object

                actualPosts.forEach(p => {
                    let type;
                    let engagement = 0;

                    if (platform.toLowerCase() === 'youtube') {
                        type = normalizeContentType('youtube', p.video_type || 'Regular');
                        const m = p.videoMetrics || {};
                        engagement = (m.likeCount || 0) + (m.commentCount || 0);
                    } else {
                        // FB/IG: Try media_type first, fallback to post_type (common in FB graph api)
                        const rawType = p.media_type || p.post_type || (p.attachment_type === 'album' ? 'CAROUSEL_ALBUM' : null);
                        type = normalizeContentType(platform, rawType);

                        const m = p.postMetrics || {};
                        engagement =
                            (m.likes || m.like || 0) +
                            (m.comments || 0) +
                            (m.shares || 0) +
                            (m.saved || 0) +
                            (m.love || 0) +
                            (m.wow || 0) +
                            (m.haha || 0) +
                            (m.sad || 0) +
                            (m.angry || 0);
                    }

                    if (bins[type]) {
                        bins[type].total += engagement;
                        bins[type].count += 1;
                    }
                });
            });
        });

        return { categories, profileAggregations };
    }, [safeDatasets]);

    // ----------------------------------------------------------------------
    // CHART DATA
    // ----------------------------------------------------------------------
    const chartData = useMemo(() => {
        const { categories, profileAggregations } = processedData;
        const profileIds = safeDatasets.map(p => p.profileId);
        const colors = generateColors(safeDatasets.length, profileIds);

        const formattedDatasets = safeDatasets.map((profile, idx) => {
            const bins = profileAggregations[idx];
            const data = categories.map(cat => {
                const bin = bins[cat];
                return bin.count > 0 ? bin.total / bin.count : 0;
            });

            return {
                label: profile.name || profile.label || "Profile",
                data: data,
                backgroundColor: colors[idx],
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl,
                platform: profile.platform,
                metricName: "Best Performing Format"
            };
        });

        return {
            labels: categories,
            datasets: formattedDatasets
        };
    }, [safeDatasets, processedData]);


    // ----------------------------------------------------------------------
    // METRICS (Identify Best Format)
    // ----------------------------------------------------------------------
    // ----------------------------------------------------------------------
    // METRICS (Identify Best Format)
    // ----------------------------------------------------------------------
    const metrics = useMemo(() => {
        const { categories, profileAggregations } = processedData;

        // HELPER: Calculate Best Format for a given set of bins
        const getBestFormat = (bins) => {
            let bestFormat = "N/A";
            let maxAvg = -1;
            categories.forEach(cat => {
                const bin = bins[cat] || { total: 0, count: 0 }; // Safety check
                const avg = bin.count > 0 ? bin.total / bin.count : 0;
                if (avg > maxAvg) {
                    maxAvg = avg;
                    bestFormat = cat;
                }
            });
            if (maxAvg <= 0) return "N/A";
            return bestFormat;
        };

        // HELPER: Process Previous Datasets (Reuse Aggregation Logic)
        const previousAggregations = (previousdatasets || []).map(profile => {
            const bins = {};
            categories.forEach(c => bins[c] = { total: 0, count: 0 });
            return { profileId: profile.profileId, bins, platform: profile.platform || 'instagram', data: profile.data || [] };
        });

        // Run aggregation for previous data
        previousAggregations.forEach(pItem => {
            const { platform, data: posts, bins } = pItem;
            posts.forEach(post => {
                let actualPosts = [];
                if (post.posts) actualPosts = post.posts;
                else if (post.videos) actualPosts = post.videos;
                else actualPosts = [post];

                actualPosts.forEach(p => {
                    let type;
                    let engagement = 0;
                    if (platform.toLowerCase() === 'youtube') {
                        type = normalizeContentType('youtube', p.video_type || 'Regular');
                        const m = p.videoMetrics || {};
                        engagement = (m.likeCount || 0) + (m.commentCount || 0);
                    } else {
                        const rawType = p.media_type || p.post_type || (p.attachment_type === 'album' ? 'CAROUSEL_ALBUM' : null);
                        type = normalizeContentType(platform, rawType);
                        const m = p.postMetrics || {};
                        engagement = (m.likes || m.like || 0) + (m.comments || 0) + (m.shares || 0) + (m.saved || 0) + (m.love || 0) + (m.wow || 0) + (m.haha || 0) + (m.sad || 0) + (m.angry || 0);
                    }
                    if (bins[type]) {
                        bins[type].total += engagement;
                        bins[type].count += 1;
                    }
                });
            });
        });

        return safeDatasets.map((profile, idx) => {
            const bins = profileAggregations[idx];
            const currentMetricValue = getBestFormat(bins);

            let previousMetricValue = "N/A";
            const prevItem = previousAggregations.find(p => p.profileId === profile.profileId);
            if (prevItem) {
                previousMetricValue = getBestFormat(prevItem.bins);
            }

            return {
                profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
                name: profile.name || profile.label || "Profile",
                platform: profile.platform,
                metricName: "Best Performing Format",
                metricValue: currentMetricValue,
                previousMetricValue: previousMetricValue,
                isQualitative: true // ✅ Qualitative Flag
            };
        });
    }, [safeDatasets, processedData, previousdatasets]);

    // ----------------------------------------------------------------------
    // LOADING STATE
    // ----------------------------------------------------------------------
    if (isLoading) {
        return (
            <Card>
                <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
                    <CircularProgress size={32} thickness={4} color="accent" />
                </MDBox>
            </Card>
        );
    }

    // ----------------------------------------------------------------------
    // TOOLTIP CONTENT
    // ----------------------------------------------------------------------
    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                Analyzes average engagement across different content formats (e.g., Reels, Static Images, Long Videos) to find what resonates best with your audience.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                - <strong>X-Axis</strong>: Content Categories (Normalized across platforms).<br />
                - <strong>Y-Axis</strong>: Average Engagement per Post.<br />
                - <strong>Highest Bar</strong>: Indicates the format yielding the best ROI.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Determine if video or static content drives more interaction. <br />
                - Identify underperforming formats to stop investing in. <br />
                - Shift your strategy to prioritize the highest-ROI format.
            </Typography>
        </div>
    );

    // ----------------------------------------------------------------------
    // RENDER
    // ----------------------------------------------------------------------
    return (
        <VerticalBarChart
            title={title}
            description={tooltipContent}
            height="19.125rem"
            chart={chartData}
            customMetrics={metrics} // ✅ Ensure metrics are used (renamed prop if necessary)
            is_derived={true} // Use derived metrics card style
            scaleType="category"
            isEntitled={isEntitled}
            featureName={featureName}
            planRequired={planRequired}
            rawData={safeDatasets}
            entitlements={entitlements} // ✅ Pass entitlements
            disableToggle={true} // ✅ Disable toggle for this chart
            yAxisTitle="Average Engagement" // ✅ Correct Y-Axis Title
        />
    );
}

OptimalContentFormatChart.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    datasets: PropTypes.array,
    previousdatasets: PropTypes.array,
    isLoading: PropTypes.bool,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
    entitlements: PropTypes.object,
};

export default OptimalContentFormatChart;
